import { sentenceCase } from "change-case";

import i18n from "@/locales/i18n";
import { checkAsset } from "@/utils/transactions/ExchangeCurrencies";
import { formatToFloat } from "@/utils/transactions/FormatTransactionValue";

type Transaction = {
  id: string;
  value: string | number;
  description?: string;
  transactionType: string;
  transactionPostedDate?: string;
  transactionDate?: string;
  createdAt?: string;
  createdTimestamp?: string;
  status?: string;
  transactionStatus?: string;
  type?: string;
  destinationAssetTypeId?: string;
  sourceAssetTypeId?: string;
  cpName?: string;
  sourceAccountIdId?: string;
  destinationAccountIdId?: string;
  sourceAccountId?: string;
  destinationAccountId?: string;
  sourceRegisteredName?: string;
  sourceFirstName?: string;
  destinationRegisteredName?: string;
  destinationFirstName?: string;
  amount?: string;
  amountToCredit?: string;
  amountToDebit?: string;
  originator?: {
    profile: {
      name: string;
    };
    accountInformation?: {
      institutionName?: string;
    };
  };
  destination?: {
    profile?: {
      name: string;
    };
  };
  sourceDetails?: {
    id: string;
    assetTypeId: string;
    amountToDebit: number;
    sourceAccountId: {
      id: string;
      customer: {
        application: {
          customerDetails: {
            firstName?: string;
            registeredName?: string;
          };
        };
      };
    };
  };
  destinationDetails?: {
    id: string;
    assetTypeId: string;
    amountToDebit: number;
    destinationAccountId: {
      id: string;
      customer: {
        application: {
          customerDetails: {
            firstName?: string;
            registeredName?: string;
          };
        };
      };
    };
  };
};

export function checkTransferType(
  transactionType: string,
  type: string,
  rail: string,
  t: any
) {
  switch (transactionType) {
    case "FEE":
      if (type === "PROCESSING_FEE") return t("common:transfer.processing");
      else if (type === "ACH_FEE") return t("common:transfer.ach");
      else if (type === "SWIFT_FEE") return t("common:transfer.swift");
      else if (type === "WIRE_FEE") return t("common:transfer.wire");
      else if (type === "CONVERSION_FEE")
        return t("common:transfer.exchangefee");
      else return "Fee";

    case "INTERNAL_TRANSFER":
      return t("common:transfer.internalTransfer");

    case "OTC_INTERNAL_TRANSFER":
      return t("common:transfer.otcInternalTransfer");

    case "FIAT_TRANSFER_OUT_RETURN":
      return t("common:transfer.return");

    case "TRANSFER_OUT_RETURN":
      return t("common:transfer.return");

    case "FIAT_TRANSFER_IN":
      if (type === "TRANSFER") return t("common:transfer.fee");
      else if (rail === "ACH") return t("common:transfer.achTransferIn");
      else if (rail === "SWIFT") return t("common:transfer.swiftTransferIn");
      else if (rail === "FEDWIRE") return t("common:transfer.wireTransferIn");
      else return t("common:transfer.deposit");

    case "CRYPTO_TRANSFER_IN":
      return t("common:transfer.cryptoTransferIn");

    case "TRANSFER_IN":
      if (rail === "CRYPTO") return t("common:transfer.cryptoTransferIn");
      else return t("common:transfer.deposit");

    case "FIAT_TRANSFER_OUT":
      if (type === "TRANSFER") return t("common:transfer.fee");
      else if (rail === "ACH") return t("common:transfer.achTransferOut");
      else if (rail === "SWIFT") return t("common:transfer.swiftTransferOut");
      else if (rail === "FEDWIRE") return t("common:transfer.wireTransferOut");
      else return t("common:transfer.withdrawal");

    case "CRYPTO_TRANSFER_OUT":
      return t("common:transfer.cryptoTransferOut");

    case "TRANSFER_OUT":
      if (rail === "CRYPTO") return t("common:transfer.cryptoTransferOut");
      else return t("common:transfer.withdrawal");

    case "TRADE":
      return t("common:transfer.exchange");
    // eslint-disable-next-line no-fallthrough
    default:
      return "Transfer";
  }
}

export function checkTransactionSenderReceiver(
  transaction: Transaction,
  accountId?: string,
  isBankStatement?: boolean,
  isOriginatorColumn?: boolean,
  isDestinationColumn?: boolean,
  customerName?: string
) {
  if (
    transaction.transactionType === "FIAT_TRANSFER_IN" ||
    transaction.transactionType === "TRANSFER_IN"
  ) {
    if (isBankStatement || isOriginatorColumn) {
      if (transaction.originator?.profile?.name) {
        return transaction.originator.profile.name;
      } else if (transaction.originator?.accountInformation?.institutionName) {
        return transaction.originator.accountInformation.institutionName;
      }
    } else {
      return customerName ?? "-";
    }
  } else if (
    transaction.transactionType === "TRANSFER_OUT" ||
    transaction.transactionType === "FIAT_TRANSFER_OUT"
  ) {
    if (isBankStatement || isDestinationColumn) {
      return (
        (transaction.destination?.profile?.name || transaction.cpName) ?? "-"
      );
    } else {
      return customerName ?? "-";
    }
  }
  //Specific return for INTERNAL_TRANSFER when exporting to XLSX. Both sender and receiver are shown.
  else if (transaction.transactionType === "INTERNAL_TRANSFER") {
    if (
      accountId &&
      accountId ===
        (transaction.destinationAccountId || transaction.destinationAccountIdId)
    ) {
      //Case to show in the bank statement and Destination column in exported XLSX
      if (isBankStatement || isOriginatorColumn) {
        return (
          (transaction.sourceFirstName || transaction.sourceRegisteredName) ??
          "-"
        ).toUpperCase();
      }
      //Opposite case to show in the Originator column in exported XLSX
      else {
        return (
          (transaction.destinationFirstName ||
            transaction.destinationRegisteredName) ??
          "-"
        ).toUpperCase();
      }
    } else if (
      accountId &&
      accountId ===
        (transaction.sourceAccountId || transaction.sourceAccountIdId)
    ) {
      //Case to show in the bank statement and Originator column in exported XLSX
      if (isBankStatement || isDestinationColumn) {
        return (
          (transaction.destinationRegisteredName ||
            transaction.destinationRegisteredName) ??
          "-"
        ).toUpperCase();
      }
      //Opposite case to show in the Originator column in exported XLSX
      else {
        return (
          (transaction.sourceFirstName || transaction.sourceRegisteredName) ??
          "-"
        ).toUpperCase();
      }
    } else {
      return "-";
    }
  } else if (
    transaction.transactionType === "TRADE" ||
    transaction.transactionType === "EXCHANGE"
  ) {
    if (isBankStatement) {
      return `${checkAsset(transaction.sourceAssetTypeId)} to ${checkAsset(
        transaction.destinationAssetTypeId
      )}`;
    } else {
      if (isOriginatorColumn) {
        if (
          accountId === transaction.sourceAccountId ||
          accountId === transaction.sourceAccountIdId
        ) {
          return customerName ?? "-";
        } else {
          return "Layer2 Financial Inc";
        }
      } else if (isDestinationColumn) {
        if (
          accountId === transaction.destinationAccountId ||
          accountId === transaction.destinationAccountIdId
        ) {
          return customerName ?? "-";
        } else {
          return "Layer2 Financial Inc";
        }
      }
    }
  } else if (transaction.transactionType === "OTC_INTERNAL_TRANSFER") {
    if (formatToFloat(transaction.amountToDebit) < 0) {
      if (isOriginatorColumn || isBankStatement) {
        return customerName ?? "-";
      } else {
        return "Layer2 Financial Inc";
      }
    } else {
      if (isDestinationColumn || isBankStatement) {
        return customerName ?? "-";
      } else {
        return "Layer2 Financial Inc";
      }
    }
  } else if (transaction.transactionType === "FEE") {
    if (isOriginatorColumn) {
      return customerName ?? "-";
    } else {
      return "Izzi Wallet";
    }
  } else if (transaction.cpName) {
    return transaction.cpName;
  } else {
    return "-";
  }
}

export function checkTransactionValueForExport(
  transaction: any,
  accountId: string,
  isOriginatorColumn: boolean
) {
  const creditTransactionTypes = [
    "CRYPTO_TRANSFER_IN",
    "TRANSFER_IN",
    "FIAT_TRANSFER_IN",
    "FIAT_TRANSFER_OUT_RETURN",
    "TRANSFER_OUT_RETURN",
  ];
  const debitTransactionTypes = ["TRANSFER_OUT", "FIAT_TRANSFER_OUT", "FEE"];
  const internalTransactionTypes = ["INTERNAL_TRANSFER"];
  const exchangeTransactionTypes = ["TRADE", "EXCHANGE"];

  if (
    debitTransactionTypes.includes(transaction.transactionType) &&
    isOriginatorColumn
  ) {
    if (transaction.value) {
      return formatToFloat(transaction.value) < 0
        ? transaction.value
        : formatToFloat(transaction.value) * -1;
    } else if (transaction.amount) {
      return formatToFloat(transaction.amount) < 0
        ? formatToFloat(transaction.amount)
        : formatToFloat(transaction.amount) * -1;
    }
  } else if (
    creditTransactionTypes.includes(transaction.transactionType) &&
    !isOriginatorColumn
  ) {
    if (transaction.value) {
      return formatToFloat(transaction.value);
    } else if (transaction.amount) {
      return formatToFloat(transaction.amount);
    }
  } else if (internalTransactionTypes.includes(transaction.transactionType)) {
    if (
      accountId ===
      (transaction.sourceAccountId || transaction.sourceAccountIdId)
    ) {
      if (isOriginatorColumn) {
        return formatToFloat(transaction.amountToDebit);
      } else {
        return "";
      }
    } else if (
      accountId ===
      (transaction.destinationAccountId || transaction.destinationAccountIdId)
    ) {
      if (!isOriginatorColumn) {
        return formatToFloat(transaction.amountToCredit);
      } else {
        return "";
      }
    }
  } else if (exchangeTransactionTypes.includes(transaction.transactionType)) {
    if (
      isOriginatorColumn &&
      (accountId === transaction.sourceAccountIdId ||
        accountId === transaction.sourceAccountId)
    ) {
      return formatToFloat(transaction.amountToDebit) * -1;
    } else if (
      !isOriginatorColumn &&
      (accountId === transaction.destinationAccountId ||
        accountId === transaction.destinationAccountIdId)
    ) {
      return formatToFloat(transaction.amountToCredit);
    } else {
      return "";
    }
  } else if (transaction.transactionType === "OTC_INTERNAL_TRANSFER") {
    if (isOriginatorColumn && formatToFloat(transaction.amountToDebit) < 0) {
      return formatToFloat(transaction.amountToDebit);
    } else if (
      !isOriginatorColumn &&
      formatToFloat(transaction.amountToDebit) > 0
    ) {
      return formatToFloat(transaction.amountToDebit);
    } else {
      return "";
    }
  }
}

export function checkTransactionValue(
  transaction: any,
  accountId: string,
  isFiat: boolean,
  currentAsset: string,
  decimalPrecision: number,
  isExport?: boolean
) {
  //Early return to FEE cases.
  if (transaction.transactionType === "FEE") {
    if (transaction.amount) {
      if (isExport) {
        return formatToFloat(transaction.amount) * -1;
      } else {
        return new Intl.NumberFormat(i18n.language, {
          style: "currency",
          currency: "USD",
          maximumFractionDigits: 2,
        }).format(formatToFloat(transaction.amount) * -1);
      }
    } else if (transaction.value) {
      if (isExport) {
        return formatToFloat(transaction.value) * -1;
      } else {
        return new Intl.NumberFormat(i18n.language, {
          style: "currency",
          currency: "USD",
          maximumFractionDigits: 2,
        }).format(formatToFloat(transaction.value) * -1);
      }
    } else {
      return "-";
    }
  }

  //Early return to INTERNAL_TRANSFER cases.
  if (
    transaction.transactionType === "INTERNAL_TRANSFER" ||
    transaction.transactionType === "OTC_INTERNAL_TRANSFER"
  ) {
    if (
      accountId ===
      (transaction.sourceAccountId || transaction.sourceAccountIdId)
    ) {
      return isFiat
        ? new Intl.NumberFormat(i18n.language, {
            style: "currency",
            currency: "USD",
            maximumFractionDigits: 2,
          }).format(formatToFloat(transaction.amountToDebit) * -1)
        : formatToFloat(transaction.amountToDebit, decimalPrecision) * -1;
    } else if (
      accountId ===
      (transaction.destinationAccountId || transaction.destinationAccountIdId)
    ) {
      return isFiat
        ? new Intl.NumberFormat(i18n.language, {
            style: "currency",
            currency: "USD",
            maximumFractionDigits: 2,
          }).format(formatToFloat(transaction.amountToCredit))
        : formatToFloat(transaction.amountToCredit, decimalPrecision);
    } else {
      return isFiat
        ? new Intl.NumberFormat(i18n.language, {
            style: "currency",
            currency: "USD",
            maximumFractionDigits: 2,
          }).format(formatToFloat(transaction.amountToDebit))
        : formatToFloat(transaction.amountToDebit, decimalPrecision);
    }
  }

  if (typeof transaction.value === "string") {
    if (isFiat) {
      return new Intl.NumberFormat(i18n.language, {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 2,
      }).format(formatToFloat(transaction.value));
    } else {
      return formatToFloat(transaction.value);
    }
  } else if (typeof transaction.value === "number") {
    if (isFiat) {
      return new Intl.NumberFormat(i18n.language, {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 2,
      }).format(transaction.value);
    } else {
      return transaction.value;
    }
  } else if (transaction.amount) {
    if (isFiat) {
      return new Intl.NumberFormat(i18n.language, {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 2,
      }).format(formatToFloat(transaction.amount));
    } else {
      return formatToFloat(transaction.amount);
    }
  } else if (
    accountId &&
    accountId === (transaction.sourceAccountIdId || transaction.sourceAccountId)
  ) {
    return formatToFloat(transaction.amountToDebit, decimalPrecision) * -1;
  } else if (
    accountId &&
    accountId ===
      (transaction.destinationAccountId || transaction.destinationAccountIdId)
  ) {
    return formatToFloat(transaction.amountToCredit, decimalPrecision);
  } else {
    return "-";
  }
}

export function checkTransactionStatus(transaction: Transaction, t: any) {
  const status = {
    AUTHORIZED: t("common:status.authorized"),
    PENDING: t("common:status.pending"),
    POSTED: t("common:status.posted"),
    CANCELLED: t("common:status.cancelled"),
    REJECTED: t("common:status.rejected"),
    SCHEDULED: t("common:status.scheduled"),
    ACCEPTED: t("common:status.accepted"),
    EXECUTED: t("common:status.posted"),
    PAID: t("common:status.posted"),
    UNCONFIRMED: t("common:status.unconfirmed"),
    FAILED: t("common:status.failed"),
  };

  return (
    status[transaction.transactionStatus || transaction.status] ??
    sentenceCase(transaction.transactionStatus || transaction.status)
  );
}

export function checkTransactionDate(transaction: Transaction) {
  return new Date(
    transaction.transactionDate || transaction.createdAt
  ).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  });
}

export function checkXlsxTransactionDate(transaction: Transaction) {
  return new Date(
    transaction.transactionDate || transaction.createdAt
  ).toLocaleString(undefined, {
    month: "numeric",
    day: "numeric",
    year: "numeric",
    hour12: false,
  });
}

export function checkXlsxTransactionTime(transaction: Transaction) {
  return new Date(
    transaction.transactionDate || transaction.createdAt
  ).toLocaleString(undefined, {
    hour12: true,
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function getTransactionsPageNumberInString(url: string): number {
  const match = url.match(/[?&]page=(\d+)/);
  return match ? parseInt(match[1], 10) : null;
}

export const propertiesToIgnore = [
  "value",
  "id",
  "transactionPostedDate",
  "transactionDate",
  "transactionStatus",
  "transactionDate",
  "transactionId",
  "categoryId",
  "isFiat",
  "currentAsset",
  "createdAt",
  "layer2TransactionId",
  "destinationMiddleName",
  "sourceMiddleName",
  "address",
  "railReference",
  "accountId",
  "memo",
  "railOriginatorMemo",
  "purpose",
  "transactionType",
  "type",
  "rail",
  "cpAccountId",
  "cpAssetTypeId",
  "cpType",
  "destinationAccountIdId",
  "destinationAccountId",
  "sourceAccountIdId",
  "sourceAccountId",
  "amount",
];
